<template>
    <div class="variant-selection-wrapper">
        <div class="d-flex flex-column">
            <span>
                <strong> Template Product: </strong>
                {{ templateProduct.templateProductName }}
            </span>
            <span>
                <strong> POD Product: </strong>
                {{ selectedProduct.title }}
            </span>
            <p>Choose a product variant and assign a POD variant to it.</p>
        </div>

        <div class="d-flex justify-content-end align-items-center mb-2">
            <strong>Wholesale Price</strong>
            <div 
                v-tippy 
                content="Copy first variant's price to all other variants"
                class="ml-1"
            >
                <button 
                    class="btn-sm" 
                    @click="copyFirstPriceToAll"                           
                >
                    <i class="fa fa-copy mr-1"></i>                
                </button>
            </div>
        </div>

        <ul class="list-group">
            <li
                v-for="(variant, idx) in templateProduct.variants"
                :id="`variant-${idx}-${variant.id}`"
                :key="`variant-${idx}-${variant.id}`"
                class="list-group-item d-flex align-items-center justify-content-between"
            >
                <span class="badge badge-customily">
                    {{ variant.title }}
                </span>

                <div v-tippy class="fulfillment-container" :content="variant.podVariantName || variant.title">
                    <div class="d-flex align-items-center">                        
                        <button class="btn btn-turquise" @click="selectVariant(variant, `variant-${idx}-${variant.id}`)">
                            Provider Variant
                        </button>                        
                        <div v-if="variant.podVariantId" class="form-control fulfillment-input ml-3">
                            <span class="fulfillment-text">{{ variant.podVariantName || 'Assigned to a different product'}}</span>
                            <div class="remove-fulfillment-icon" @click="removeVariantSelection(variant)">
                                <i class="fa fa-times-circle" />
                            </div>
                        </div>
                        <div class="price-input-container d-flex flex-column justify-content-center ml-3">                            
                            <b-form-input
                                v-model="variant.wholesalePrice"
                                type="number"           
                                min="0"
                                size="sm"
                                style="width: 80px;"
                            />
                        </div>
                    </div>
                </div>
            </li>
        </ul>
    </div>
</template>

<script lang="ts" setup>
import { TemplateProductVariant, Variant } from '@/api/typings/templateProducts';
import { GenericPodProduct } from '@/api/typings/podProviders';

const emit = defineEmits(['open-variant-matcher']);

const props = defineProps<{
    templateProduct: TemplateProductVariant;
    selectedProduct: GenericPodProduct;
    provider: string;
}>();

const removeVariantSelection = (variant: Variant) => {
    variant.podVariantId = undefined;
    variant.podVariantName = undefined;    
};

const selectVariant = (variant: Variant, key: string) => {
    emit('open-variant-matcher', variant, key);
};

const copyFirstPriceToAll = () => {
    if (!props.templateProduct.variants.length) return;
    
    const firstPrice = props.templateProduct.variants[0]?.wholesalePrice;
    if (firstPrice === undefined || firstPrice === null) return;
    
    // Update all variants with the first price
    props.templateProduct.variants.forEach((variant, index) => {
        if (index > 0) { // Skip the first variant since it's the source
            variant.wholesalePrice = firstPrice;
        }
    });
    
    // Force Vue to re-render by triggering reactivity
    // This creates a new reference to the variants array, forcing Vue to update
    props.templateProduct.variants = [...props.templateProduct.variants];
};
</script>

<style lang="scss" scoped>
.variant-selection-wrapper {
    .list-group-item {
        cursor: unset;
        &:hover {
            background: transparent !important;
        }
    }

    .badge-customily {
        padding: 0.25rem;
        background: #5b44bd;
        font-weight: normal;
        font-size: 14px;
    }

    .fulfillment-container {
        display: flex;

        .price-input-container {
            display: flex;
            flex-direction: column;
        }

        .fulfillment-input {
            width: 350px;
            pointer-events: none;

            &.highlight {
                border-color: #88c3d1;
            }
            .remove-fulfillment-icon {
                display: flex;
                float: right;
                height: 100%;
                align-items: center;
                cursor: pointer;
                pointer-events: all;
            }
            .fulfillment-text {
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                width: calc(100% - 30px);
                display: inline-block;
            }
        }
    }
}
</style>
